import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { TopRatedView } from '../rating/TopRatedPage.js';

interface Resolved {
  rootId: string;
  albumKey: CryptoKey;
}

// Viewer-mode top-rated gallery. Identical UX to the owner page; the only
// difference is how we resolve K_album — via the matching incoming share
// rather than via the owner's listAlbums.
export function SharedTopRatedPage() {
  const { rootId } = useParams<{ rootId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [resolved, setResolved] = useState<Resolved | { error: string } | null>(null);

  useEffect(() => {
    if (!rootId) return;
    let cancelled = false;
    setResolved(null);
    void (async () => {
      try {
        const { shares } = await metadata.listIncomingShares(session.jwt);
        const share = shares.find((s) => s.rootAlbumId === rootId);
        if (!share) {
          if (!cancelled) setResolved({ error: 'condivisione non trovata' });
          return;
        }
        const albumKey = await getAlbumKey(rootId, share.wrappedAlbumKey, session.rsaPriv);
        if (cancelled) return;
        setResolved({ rootId, albumKey });
      } catch (err) {
        if (!cancelled) setResolved({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rootId, session.jwt, session.rsaPriv, metadata]);

  if (!rootId) return null;
  if (resolved === null) return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in resolved)
    return <p className="py-6 text-sm text-error">Errore: {resolved.error}</p>;

  return (
    <TopRatedView
      rootId={resolved.rootId}
      albumKey={resolved.albumKey}
      backTo={`/shared/${encodeURIComponent(resolved.rootId)}`}
      backLabel="Radice"
      eyebrow="viewer · solo lettura · classifica"
    />
  );
}
